import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import FlexPanel from '../FlexPanel';
import Api from '../../services/ApiService';
import Auth from '../../services/AuthService';
import FileService from '../../services/FileService';
import Form from '../form/Form';
import { modal } from '../modal/ModalEmitter';

class ArchiveUpload extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      uploadedFiles: [],
    };

    this.submit = this.submit.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.selectedBranch = null;

    this.layout = [
      {
        name: 'branchId',
        label: 'Branch',
        validation: {
          type: 'db',
          modelName: 'Branch',
          sortBy: 'name',
          filter: this.getBranches(),
        },
        md: 4,
        required: true,
      },
      {
        name: 'docTypeId',
        label: 'Document Type',
        dependencies: {
          and: [{ '==': ['branchId', ''] }],
          action: 'hide',
        },
        validation: {
          type: 'db',
          modelName: 'ArchiveDocType',
          sortBy: 'name',
          filter: data => {
            return _.filter(data, obj => {
              if (!this.selectedBranch) {
                return false;
              }

              if (!this.props.docTypes[this.selectedBranch].includes(parseInt(obj.id))) {
                return false;
              }

              // We do not support uploading to "Reports" so hide it
              return !(obj.name === 'Reports' && !this.docTypes[obj.id].pattern);
            });
          },
        },
        md: 4,
        required: true,
        events: {
          blur: {
            update: {
              method: {
                name: 'getPattern',
                keys: ['docTypeId'],
              },
            },
          },
        },
      },
      {
        name: 'date',
        label: 'Date',
        type: 'date',
        md: 4,
        required: true,
        dependencies: {
          or: [{ '!=': ['pattern', ''] }, { '==': ['branchId', ''] }],
          action: 'hide',
        },
      },
      {
        control: 'Upload',
        collection: true,
        name: 'files',
        label: 'Upload Documents',
        type: 'file',
        required: true,
        clearOnSubmit: true,
        dependencies: {
          and: [{ '==': ['branchId', ''] }],
          action: 'hide',
        },
      },
      {
        name: 'pattern',
        type: 'string',
        data: '',
        hidden: true,
      },
    ];
  }

  componentDidMount() {
    Api.get('archive/docTypes').then(res => {
      this.docTypes = _.keyBy(res.data, 'id');

      this.setState({ loading: false });
    });
  }

  /**
   * Get branches user has Archive Upload access too
   * @returns {Array}
   */
  getBranches() {
    const { permissions, docTypes } = this.props;

    const branches = [];
    _.each(permissions, (perms, branchId) => {
      // check if user has permissions and doc types for branch before showing
      if ((perms.UPA3 || perms.UPA2 || Auth.isDsiSupport()) && docTypes[branchId] && docTypes[branchId].length) {
        branches.push(parseInt(branchId));
      }
    });

    return branches;
  }

  handleChange({ form }) {
    this.selectedBranch = parseInt(form.branchId.data);
  }

  submit(data) {
    const { permissions } = this.props;

    const payload = {};
    for (const field of data) {
      if (field.data === '') {
        continue;
      }
      payload[field.name] = field.data;
    }

    if (!Auth.isDsiSupport() && payload.branchId) {
      if (
        !permissions[payload.branchId].UPA2 &&
        !this.props.docTypes[payload.branchId].includes(parseInt(payload.docTypeId))
      ) {
        return Promise.reject(new Error('No access for selected Document Type and Branch combination'));
      }
    }

    return Api.post('archive', payload).then(res => {
      if (res.data.uploadedFiles) {
        this.setState({ uploadedFiles: res.data.uploadedFiles });
      }
      return Promise.resolve(res);
    });
  }

  download(filePath) {
    const newWin = window.open('', '_blank');
    newWin.document.write('Loading...');

    Api.get('archive', {
      params: {
        filePath,
      },
    }).then(res => {
      newWin.location.href = FileService.downloadPath(res.data.fileName);
    });
  }

  renderUploadedFiles() {
    const { uploadedFiles } = this.state;

    if (!uploadedFiles.length) {
      return null;
    }

    return (
      <div className="col-md-12">
        <FlexPanel header={`Uploaded Files (${uploadedFiles.length})`}>
          {uploadedFiles.map((file, i) => {
            return (
              <div
                className="uploaded-file"
                key={`uploaded-files-${i}`}
                onClick={() => {
                  this.download(file.name);
                }}
              >
                <i className="zmdi zmdi-file-text zmdi-hc-fw zmdi-hc-lg pull-left file-icon" />
                <div className="file-info">
                  <div>
                    <a>{file.name}</a>
                  </div>
                  <div className="file-meta">
                    <span>
                      <b>Bytes:</b> {file.size}
                    </span>
                    <span>
                      <b>Updated:</b> {file.updated}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </FlexPanel>
      </div>
    );
  }

  renderPermissions() {
    const { branches, permissions, docTypes } = this.props;

    if (!branches && !branches.lenght) {
      return 'No upload permissions found';
    }

    return branches.map(branch => {
      const perms = permissions[branch.id];

      const getBranchDocTypes = () => {
        return Object.keys(this.docTypes).map(docTypeId => {
          const hasDocType =
            docTypes[branch.id] && docTypes[branch.id].length && docTypes[branch.id].includes(parseInt(docTypeId));
          const classes = classNames({ label: true, 'label-success': hasDocType, 'label-danger': !hasDocType });
          return (
            <span className={classes} style={{ marginRight: '5px', marginBottom: '5px', float: 'left' }}>
              {this.docTypes[docTypeId].name}
            </span>
          );
        });
      };

      return (
        <div key={branch.id} style={{ marginBottom: '10px' }}>
          <div>
            <b>{branch.name}</b>
          </div>
          {!perms.UPA3 && !perms.UPA2 && (
            <div>
              <i>No permission to upload for this branch</i>
            </div>
          )}
          {getBranchDocTypes()}
          <div style={{ clear: 'both' }} />
        </div>
      );
    });
  }

  handleSummaryButton() {
    modal.info({
      title: 'Permission Summary',
      message: this.renderPermissions(),
      size: 'lg',
      style: 'top',
    });
  }

  render() {
    if (this.state.loading) {
      return <div>Loading</div>;
    }

    const customActions = [
      {
        label: 'Permission Summary',
        action: this.handleSummaryButton.bind(this),
        style: 'info',
        title: 'Show upload permissions',
      },
    ];

    return (
      <div className="content-container" id="archive-upload">
        <Form
          layout={this.layout}
          submitAll
          submit={this.submit}
          onChange={this.handleChange}
          customActions={customActions}
        />
        {this.renderUploadedFiles()}
      </div>
    );
  }
}

ArchiveUpload.propTypes = {
  branches: PropTypes.array,
  docTypes: PropTypes.object,
  permissions: PropTypes.object,
};

function mapStateToProps(state) {
  return {
    branches: state.app.user.branches,
    docTypes: state.app.user.archive.docTypes,
    permissions: state.app.user.permissions,
  };
}

export default connect(mapStateToProps)(ArchiveUpload);
